import { Button, Col, Form, Row, Select, Spin, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { StateProps, WhInfo } from "common/interface";
import { h1 } from "common/styles";
import { variables } from "common/variables";
import { AppLayout } from "components/AppLayout";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { logout, fetchWhDivisions, updateWarehouseId, updateWhDivisionId } from "store/app/appSlice";
import styled from "styled-components";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`;

const StyledRow = styled(Row)`
  height: 100%;
`;

const StyledH1 = styled.h1`
  ${h1};
  text-align: center;
`;

const StyledSelect = styled(Select)`
  border-radius: 10px;
  width: 20rem;
`;

export const WarehouseSelectionPage = () => {
  useDocumentTitle("Select Warehouse");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, status, whId, whDivId, whDivisions, divisionsStatus } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  const [form] = useForm();

  // Fetch divisions for the pre-selected warehouse on mount
  useEffect(() => {
    if (whId) {
      dispatch(fetchWhDivisions(whId));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill only while the field is empty. A later division response must not
  // write the previously saved id back over a division the user just picked.
  useEffect(() => {
    if (whDivisions.length === 0) return;
    const current = form.getFieldValue("whDivId");
    const currentStillListed =
      current != null && whDivisions.some((division) => division.id === Number(current));
    if (currentStillListed) return;
    if (whDivId != null && whDivisions.some((division) => division.id === whDivId)) {
      form.setFieldsValue({ whDivId });
      return;
    }
    // Drop an id that belongs to a previous warehouse so Proceed cannot
    // submit it after the options have changed.
    if (current != null) {
      form.setFieldsValue({ whDivId: undefined });
    }
  }, [form, whDivId, whDivisions]);

  const handleWarehouseChange = (newWhId: number) => {
    // Clear division when warehouse changes
    form.setFieldsValue({ whDivId: undefined });
    dispatch(fetchWhDivisions(newWhId));
  };

  const handleProceed = async (values: { whId: number; whDivId: number }) => {
    try {
      const nextWhId = Number(values.whId);
      const nextDivId = Number(form.getFieldValue("whDivId") ?? values.whDivId);
      if (!Number.isFinite(nextWhId) || !Number.isFinite(nextDivId)) return;
      // Warehouse update clears the division when the warehouse actually
      // changes, so the new division has to be stored after it.
      dispatch(updateWarehouseId(nextWhId));
      dispatch(updateWhDivisionId(nextDivId));
      const from = (location?.state as StateProps)?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (e) {
      console.log(e);
    }
  };

  const goToLogin = () => {
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const isDivisionsLoading = divisionsStatus === "loading";
  const hasDivisions = whDivisions.length > 0;
  const selectedWh = form.getFieldValue("whId") ?? whId;

  return (
    <AppLayout style={{ padding: 0 }}>
      <StyledRow justify="center">
        <Col
          md={12}
          xs={24}
          style={{
            backgroundColor: variables.wmBackgroundColorLight1,
            width: "100%",
            display: "flex",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Row
            style={{
              display: "flex",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Col>
              <FormContainer>
                <StyledH1>Select Warehouse & Division</StyledH1>

                {user.user?.mappedWh?.length ? (
                  <Form
                    form={form}
                    onFinish={handleProceed}
                    initialValues={{ whId }}
                    layout="vertical"
                  >
                    {/* Step 1: Warehouse */}
                    <Form.Item
                      name="whId"
                      label={<Typography.Text strong>Warehouse</Typography.Text>}
                      rules={[{ required: true, message: "Please select a warehouse." }]}
                    >
                      <StyledSelect
                        placeholder="Select warehouse"
                        size="large"
                        onChange={(val) => handleWarehouseChange(val as number)}
                        options={(user.user?.mappedWh ?? []).map((whInfo: WhInfo) => ({
                          value: whInfo.id,
                          label: `${whInfo.name} (${whInfo.id})`,
                        }))}
                        loading={status === "loading"}
                      />
                    </Form.Item>

                    {/* Step 2: Division — shown once a warehouse is selected */}
                    <Form.Item
                      name="whDivId"
                      label={<Typography.Text strong>Division</Typography.Text>}
                      rules={[{ required: true, message: "Please select a division." }]}
                    >
                      <StyledSelect
                        placeholder={
                          isDivisionsLoading
                            ? "Loading divisions…"
                            : !selectedWh
                            ? "Select a warehouse first"
                            : "Select division"
                        }
                        size="large"
                        disabled={!selectedWh || isDivisionsLoading || (!hasDivisions && divisionsStatus !== "idle")}
                        loading={isDivisionsLoading}
                        notFoundContent={
                          isDivisionsLoading ? (
                            <Spin size="small" />
                          ) : divisionsStatus === "failed" ? (
                            "Failed to load divisions"
                          ) : (
                            "No divisions found"
                          )
                        }
                        options={whDivisions.map((div) => ({
                          value: div.id,
                          label: `${div.name} (${div.city})`,
                        }))}
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        block
                        style={{ borderRadius: "10px", height: "40px" }}
                        loading={status === "loading"}
                        disabled={isDivisionsLoading}
                      >
                        Proceed
                      </Button>
                    </Form.Item>
                  </Form>
                ) : (
                  <>
                    <Typography.Text>
                      Sorry, you do not have any warehouse assigned. Please contact support.
                    </Typography.Text>
                    <Button
                      block
                      size="large"
                      style={{ borderRadius: "10px", height: "40px", marginTop: "30px" }}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                      onClick={goToLogin}
                    >
                      Go to Login
                    </Button>
                  </>
                )}
              </FormContainer>
            </Col>
          </Row>
        </Col>
      </StyledRow>
    </AppLayout>
  );
};
